const getFieldError = (errors, path) => {
  if (!errors || !path) return null;
  const parts = String(path).split(".");
  let current = errors;
  for (const part of parts) {
    if (current == null) return null;
    current = current[part];
  }
  if (!current) return null;
  if (current.message || current.type) return current;
  return null;
};

export const flattenFormErrorPaths = (errors, prefix = "") => {
  if (!errors || typeof errors !== "object") return [];

  return Object.keys(errors).flatMap((key) => {
    const path = prefix ? `${prefix}.${key}` : key;
    const value = errors[key];

    if (value?.message || value?.type) {
      return [path];
    }

    if (value && typeof value === "object") {
      return flattenFormErrorPaths(value, path);
    }

    return [];
  });
};

const queryFieldElement = (root, fieldPath) => {
  if (!root || !fieldPath) return null;

  const escaped = CSS.escape(fieldPath);
  const selectors = [
    `[name="${escaped}"]`,
    `input[name="${escaped}"]`,
    `select[name="${escaped}"]`,
    `textarea[name="${escaped}"]`,
  ];

  for (const selector of selectors) {
    const match = root.querySelector(selector);
    if (match) {
      return match.closest(".min-w-0") || match.closest("div") || match;
    }
  }

  return null;
};

const getScrollContainer = (root) => {
  if (!root) return null;
  if (root.classList?.contains("custom-scroll")) return root;
  return root.closest(".custom-scroll") || root;
};

const SCROLL_TOP_PADDING = 24;
const SCROLL_VIEWPORT_RATIO = 0.2;

const isScrollableY = (node) => {
  if (!node || node.nodeType !== Node.ELEMENT_NODE) return false;
  if (node === document.body || node === document.documentElement) return false;
  if (node.scrollHeight <= node.clientHeight + 1) return false;

  const { overflowY } = window.getComputedStyle(node);
  return overflowY === "auto" || overflowY === "scroll" || overflowY === "overlay";
};

/**
 * Every scroll container wrapping the element, innermost first, up to and
 * including the boundary. Advance mode nests a breakdown list inside the modal
 * body, so scrolling only the outer container leaves the field clipped.
 */
const getScrollableAncestors = (element, boundary) => {
  const containers = [];
  let node = element?.parentElement || null;

  while (node) {
    if (isScrollableY(node)) containers.push(node);
    if (node === boundary) break;
    node = node.parentElement;
  }

  if (boundary && !containers.includes(boundary) && isScrollableY(boundary)) {
    containers.push(boundary);
  }

  return containers;
};

const getScrollTopFor = (container, element) => {
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const padding = Math.max(
    SCROLL_TOP_PADDING,
    container.clientHeight * SCROLL_VIEWPORT_RATIO
  );

  return Math.max(
    0,
    elementRect.top - containerRect.top + container.scrollTop - padding
  );
};

const focusField = (element) => {
  if (!element) return;

  const focusTarget =
    element.matches?.("input, select, textarea, button")
      ? element
      : element.querySelector?.("input, select, textarea, button");

  if (focusTarget && typeof focusTarget.focus === "function") {
    try {
      focusTarget.focus({ preventScroll: true });
    } catch {
      focusTarget.focus();
    }
  }
};

export const scrollElementIntoView = (element, scrollContainer) => {
  if (!element) return;

  const boundary = getScrollContainer(scrollContainer);
  const containers = getScrollableAncestors(element, boundary);

  if (containers.length) {
    containers.forEach((container, index) => {
      const top = getScrollTopFor(container, element);
      if (index === containers.length - 1) {
        container.scrollTo({ top, behavior: "smooth" });
        return;
      }
      // Inner panels move the element, so apply them instantly to keep the
      // next container's measurement accurate.
      container.scrollTop = top;
    });

    focusField(element);
    return;
  }

  const container = boundary || element;

  if (
    container !== document.body &&
    container !== document.documentElement &&
    typeof container.scrollTo === "function"
  ) {
    container.scrollTo({
      top: getScrollTopFor(container, element),
      behavior: "smooth",
    });
  } else {
    element.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  focusField(element);
};

const collectCandidateElements = (root, orderedFieldPaths = [], errors = {}) => {
  const candidates = [];

  orderedFieldPaths.forEach((path) => {
    if (!getFieldError(errors, path)) return;
    const element = queryFieldElement(root, path);
    if (element) candidates.push(element);
  });

  if (!candidates.length) {
    flattenFormErrorPaths(errors).forEach((path) => {
      const element = queryFieldElement(root, path);
      if (element) candidates.push(element);
    });
  }

  if (!candidates.length) {
    root
      .querySelectorAll(
        [
          ".advance-mode-field-error--visible",
          ".advance-mode-time-range-shared-error",
          ".text-rose-600",
          ".text-red-500",
          ".text-red-400",
        ].join(", ")
      )
      .forEach((node) => candidates.push(node));
  }

  return candidates;
};

const pickTopMostElement = (elements = []) => {
  if (!elements.length) return null;

  return [...elements].sort((a, b) => {
    if (a === b) return 0;
    return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING
      ? -1
      : 1;
  })[0];
};

export const scrollToFirstFormError = ({
  container,
  errors = {},
  orderedFieldPaths = [],
  fallbackSelector = null,
}) => {
  const root = container || document.body;
  const scrollContainer = getScrollContainer(root) || root;
  const target = pickTopMostElement(
    collectCandidateElements(root, orderedFieldPaths, errors)
  );

  if (target) {
    scrollElementIntoView(target, scrollContainer);
    return true;
  }

  if (fallbackSelector) {
    const fallback = root.querySelector(fallbackSelector);
    if (fallback) {
      scrollElementIntoView(fallback, scrollContainer);
      return true;
    }
  }

  return false;
};

/**
 * Scrolling is deferred until after validation resolves, so the `formState`
 * captured by the caller can be a pre-validation snapshot. Omitting it makes
 * react-hook-form read its live internal state instead.
 */
const isFieldInvalid = (getFieldState, formState, path) =>
  getFieldState(path).invalid || getFieldState(path, formState).invalid;

export const getFirstInvalidFieldPath = (
  getFieldState,
  formState,
  fieldPaths = []
) => fieldPaths.find((path) => isFieldInvalid(getFieldState, formState, path)) || null;

export const scrollToFirstInvalidField = ({
  container,
  fieldPaths = [],
  getFieldState,
  formState,
  fallbackSelector = null,
}) => {
  const root = container || document.body;
  const scrollContainer = getScrollContainer(root) || root;

  for (const path of fieldPaths) {
    if (!isFieldInvalid(getFieldState, formState, path)) continue;
    const element = queryFieldElement(root, path);
    if (element) {
      scrollElementIntoView(element, scrollContainer);
      return true;
    }
  }

  return scrollToFirstFormError({
    container: root,
    errors: formState?.errors,
    orderedFieldPaths: fieldPaths,
    fallbackSelector,
  });
};

export const getBasicModeValidationFieldOrder = (
  formValues = {},
  { isShiftSplit = false } = {}
) => {
  const splits = Array.isArray(formValues.splitShifts)
    ? formValues.splitShifts
    : [];
  // Parent Service Category / line item UI is hidden in split mode — do not
  // validate those root fields or Create is blocked with no visible error.
  const splitMode = Boolean(isShiftSplit) && splits.length > 0;

  const paths = [
    "shift_date",
    "start_time",
    "end_time",
    "pt_id",
    "worker_id",
  ];

  if (!splitMode) {
    paths.push("invoice_service_type_id", "invoice_item_id");
  }

  splits.forEach((_, index) => {
    paths.push(
      `splitShifts.${index}.end_time`,
      `splitShifts.${index}.invoice_service_type_id`,
      `splitShifts.${index}.invoice_item_id`
    );
  });

  return paths;
};
